<!--todo: implement "education" styling for this form-->
<template>
  <TemplateBase>
    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="true" />
    </template>

    <div class="grid grid-cols-12 v-gradient-bg v-gradient-bg--violet">
      <section class="col-span-full relative z-20 container z-grid v-page-section-container-padding row-start-2 col-span-full relative z-20 mb-6 mt-6 sm:mb-12 sm:mt-12">
        <!--
          `s-education` re-declares the --fk-* colour variables for everything inside it, so it belongs
          on an ancestor of the whole form rather than on a single input.
        -->
        <div class="s-education relative z-10 py-6 z-grid__content col-span-full 2xl:col-span-10 2xl:col-start-2 bg-white rounded-3xl">
          <h1 class="text-3xl sm:text-4xl md:text-5xl font-semibold v-leading-small text-app-secondary-7-violet--purple-heart mb-6">{{ t_('title') }}</h1>
          <FormKit v-slot="{ state: { loading } }" v-model="user" type="form" :actions="false" @submit="submitHandler">
            <div class="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-x-4">
              <FormKit type="text" name="schoolName" :placeholder="t_('school-name')" validation="required:trim" :label="t_('school-name')" />
              <FormKit name="schoolType" type="select" :options="schoolTypeOptions" validation="required" :placeholder="t_('school-type')" :label="t_('school-type')" />
            </div>
            <div class="grid grid-cols-12 gap-0 sm:gap-x-4">
              <FormKit
                type="text"
                name="street"
                :placeholder="t_('street')"
                validation="bail|required:trim|length:3"
                :label="t_('street')"
                :classes="{ outer: 'col-span-12 sm:col-span-6' }"
              />
              <FormKit
                type="text"
                name="houseNumber"
                :placeholder="t_('house-number')"
                validation="bail|required:trim|length:0,12"
                :classes="{ outer: 'col-span-12 sm:col-span-3' }"
                :label="t_('house-number')"
              />
              <FormKit
                type="text"
                name="houseNumberAddition"
                :placeholder="t_('house-number-addition')"
                :classes="{ outer: 'col-span-12 sm:col-span-3' }"
                :label="t_('house-number-addition')"
              />
            </div>
            <div class="grid grid-cols-1 gap-0 sm:gap-x-4 sm:grid-cols-2">
              <FormKit type="text" name="zipCode" :placeholder="t_('zip-code')" validation="bail|required:trim|length:6" :label="t_('zip-code')" />
              <FormKit type="text" name="city" :placeholder="t_('city')" validation="bail|required:trim|length:3" :label="t_('city')" />
            </div>

            <!-- The form-level error message itself is rendered by FormKit's own <FormKit type="form">
                 (its default schema always includes a messages section right after this slot content);
                 this decorative divider just needs its own visibility flag since it isn't part of that. -->
            <hr v-show="hasFormError" class="my-4 border-t-2 border-solid border-app-primary-1-red--torch" />

            <div v-show="successMessages.length">
              <p v-for="(message, index) in successMessages" :key="index" class="my-4 p-2 bg-app-green-200 border-2 border-app-green rounded-md">{{ message }}</p>
            </div>

            <hr class="mb-4 border-t border-solid border-white" />

            <div class="flex flex-wrap items-center justify-between gap-4">
              <MoleculeButton :url="redirect" :enable-icon-left="false" class="group mb-6" background-color="bg-black hover:bg-app-secondary-7-violet--purple-heart">
                {{ t_('back') }}
              </MoleculeButton>

              <FormKit type="submit" :disabled="loading" :classes="{ input: 'group inline-flex items-center' }">
                {{ t_('update') }}
                <AtomIcon
                  :icon="loading ? 'app-loader' : 'arrow-right'"
                  class="ml-8 w-5 h-5"
                  :class="{ 'transform group-hover:translate-x-1 transition duration-200': !loading }"
                />
              </FormKit>
            </div>
          </FormKit>
        </div>
      </section>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'

// setupTix internally uses Pinia stores / useRuntimeConfig / useRoute - must be called before any await.
const { tixUser } = setupTix()
const bridgeFetch = useBridgeFetch()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const hasFormError = ref(false)
const successMessages = ref<string[]>([])
/** The education profile fields the form edits, sent to the bridge as-is. */
const user = ref<{ schoolType?: string | number; [key: string]: unknown }>({})

const { t_ } = setupTranslationPrefixer('Education', 'UserUpdateForm')

const schoolTypeOptions = Object.entries(educationSchoolTypes).map(([tagId, translationKey]) => ({
  value: tagId,
  label: t_(translationKey),
}))

// if purchase url is not present: either redirect back to education detail page or homepage as last fallback
const redirect = route.query?.returnPath ? localePath({ path: String(route.query.returnPath), query: { showModal: 1 } }) : localePath({ name: 'index' })

const submitHandler = async (data: CmsData, node: FormKitNode) => {
  const { id, email, hash } = unref(tixUser)

  // Clear errors and successes before saving
  node.clearErrors()
  hasFormError.value = false
  successMessages.value = []

  if (user.value.schoolType !== undefined) {
    // convert string to int, or symfony serializer will throw a "type mismatch" error
    user.value.schoolType = parseInt(String(user.value.schoolType))
  }

  let result = false

  // catch any errors from bridge and show a generic error message
  try {
    const data = await bridgeFetch(`/public/education/customer/${id}/email/${email}?hash=${hash}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
      },
      body: JSON.stringify(user.value),
    })
    result = data?.result
  } catch {
    // Ignored: falls through to the generic error message below.
  }

  // if PUT request is successful, it will return a 204 http code with empty body. 400 if errors occurred
  if (result !== true) {
    // data?.errors is also available if there are "Symfony Validations"
    node.setErrors([t_('generic-error')])
    hasFormError.value = true
  } else {
    successMessages.value = [t_('generic-success')]

    // forward to tix purchase url when user has successfully updated form. "redirect" will be used as fallback
    const purchaseUrl = route.query?.purchaseUrl

    if (purchaseUrl) {
      // tix purchase url is external
      window.location.href = String(purchaseUrl)
    } else {
      router.push({ path: redirect })
    }
  }
}
</script>

<style lang="scss" scoped>
.v-leading-small {
  line-height: 1.15;
}
</style>
